import { useCallback, useEffect, useState } from 'react'
import { getSupabaseClient } from '@/repository/supabase/client'
import {
  acceptInvite as acceptInviteApi,
  createInvite,
  fetchPendingInvites,
  fetchRoster,
  removeMember,
  revokeInvite,
  setDisplayName,
  type HouseholdMember,
  type PendingInvite,
} from '@/repository/supabase/householdApi'
import { buildInviteLink } from '@/lib/householdInvite'
import { useAuthStore } from '@/auth/authStore'
import { useAuth } from '@/auth/useAuth'
import { useHouseholdStore } from '@/store/householdStore'

/**
 * v0.8.51 — Facade for the Household panel (household sharing Phase 2).
 *
 * Loads the active workspace's roster (+ pending invites for owners) only
 * while `enabled` — the panel is the sole consumer, so there's no store and
 * no background fetching for users who never open it.
 *
 * Membership changes that move the CURRENT user between workspaces
 * (leaving a household) re-run workspace resolution via
 * `retryWorkspaceResolve`, which rebinds the cloud repos and bumps
 * `repoVersion` so every facade reloads against the new workspace.
 */
export function useHousehold(enabled: boolean) {
  const { workspaceId, retryWorkspaceResolve } = useAuth()
  const myUserId = useAuthStore((s) => s.session?.user.id ?? null)
  const [members, setMembers] = useState<HouseholdMember[]>([])
  const [invites, setInvites] = useState<PendingInvite[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const me = members.find((m) => m.userId === myUserId) ?? null
  const isOwner = me?.role === 'owner'

  const refresh = useCallback(async () => {
    const client = getSupabaseClient()
    if (!client || !workspaceId) return
    setIsLoading(true)
    setError(null)
    try {
      const roster = await fetchRoster(client, workspaceId)
      setMembers(roster)
      // v0.8.53 — keep ownership-tag labels (Registry / Dashboard) in step
      // with renames and removals made here.
      useHouseholdStore.getState().setMembers(workspaceId, roster)
      const amOwner = roster.some((m) => m.userId === myUserId && m.role === 'owner')
      setInvites(amOwner ? await fetchPendingInvites(client, workspaceId) : [])
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setIsLoading(false)
    }
  }, [workspaceId, myUserId])

  useEffect(() => {
    if (enabled) void refresh()
  }, [enabled, refresh])

  /** Creates an invite and returns the shareable link. Throws with user-facing copy. */
  const invite = useCallback(
    async (email: string): Promise<string> => {
      const client = getSupabaseClient()
      if (!client || !workspaceId) throw new Error('Sign in to invite household members')
      const token = await createInvite(client, workspaceId, email)
      void refresh()
      return buildInviteLink(window.location.origin, token)
    },
    [workspaceId, refresh]
  )

  const revoke = useCallback(
    async (inviteId: string) => {
      const client = getSupabaseClient()
      if (!client) return
      await revokeInvite(client, inviteId)
      await refresh()
    },
    [refresh]
  )

  const remove = useCallback(
    async (userId: string) => {
      const client = getSupabaseClient()
      if (!client || !workspaceId) return
      await removeMember(client, workspaceId, userId)
      if (userId === myUserId) {
        // Left the household — fall back to the user's own workspace.
        await retryWorkspaceResolve()
        return
      }
      await refresh()
    },
    [workspaceId, myUserId, refresh, retryWorkspaceResolve]
  )

  const rename = useCallback(
    async (name: string) => {
      const client = getSupabaseClient()
      if (!client || !workspaceId) return
      await setDisplayName(client, workspaceId, name)
      await refresh()
    },
    [workspaceId, refresh]
  )

  return {
    members,
    invites,
    me,
    isOwner,
    isLoading,
    error,
    refresh,
    invite,
    revoke,
    remove,
    rename,
  }
}

/** Redeems an invite token for the signed-in user and rebinds to the joined workspace. */
export function useAcceptInvite() {
  const { retryWorkspaceResolve } = useAuth()
  return useCallback(
    async (token: string): Promise<void> => {
      const client = getSupabaseClient()
      if (!client) throw new Error('Cloud sync is not configured for this build')
      await acceptInviteApi(client, token)
      // Resolution prefers the workspace with the most members, so the
      // household wins over the invitee's own Personal workspace.
      await retryWorkspaceResolve()
    },
    [retryWorkspaceResolve]
  )
}
